import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { useRouter } from "../lib/router.js";
import { useSession, type SessionUser } from "../lib/session.js";
import { Logo, PageSpinner } from "../components/ui.js";

interface InviteInfo {
  valid: boolean;
  role: string;
  email: string | null;
  emailLocked: boolean;
  expiresAt: string;
}

export function AcceptInvitePage({ token }: { token: string }) {
  const { setUser } = useSession();
  const { navigate } = useRouter();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await api.get<InviteInfo>(`/api/invites/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setInvite(info);
        if (info.email) setEmail(info.email);
      } catch {
        if (!cancelled) setInvite(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.post<{ user: SessionUser }>(
        `/api/invites/${encodeURIComponent(token)}/accept`,
        {
          username,
          password,
          displayName: displayName || undefined,
          email: email || undefined
        }
      );
      setUser(result.user);
      navigate("/today", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="auth-layout">
        <PageSpinner />
      </div>
    );
  }

  if (!invite || !invite.valid) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <div className="auth-brand">
            <Logo size={88} />
          </div>
          <h1 className="auth-title">Invitation not valid</h1>
          <p className="auth-sub">
            This invitation link is invalid, has expired, has already been used,
            or was revoked. Ask an administrator for a new one.
          </p>
          <button className="btn btn-block" onClick={() => navigate("/login")}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-brand">
          <Logo size={88} />
          <span className="auth-tagline">Build the body. Build the system.</span>
        </div>
        <h1 className="auth-title">Accept your invitation</h1>
        <p className="auth-sub">
          You've been invited to ClovaForm as a <strong>{invite.role}</strong>.
          Create your account to continue.
        </p>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="displayName">Display name (optional)</label>
            <input
              id="displayName"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="field">
            <label htmlFor="email">
              Email {invite.emailLocked ? "(set by your invitation)" : "(optional)"}
            </label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              disabled={invite.emailLocked}
            />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <span className="hint">At least 10 characters.</span>
          </div>
          <div className="field">
            <label htmlFor="confirm">Confirm password</label>
            <input
              id="confirm"
              className="input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}
